// src/pages/DebtStudio/components/Reports/BondSummaryReport.jsx
import React, { useMemo } from "react";
import ExcelGrid from "components/ExcelGrid";
import { fmtTrunc3 } from "pages/DebtStudio/utils/formatters";

/**
 * BondSummaryReport
 * --------------------------------------------
 * Displays a roll-up of serial and term bonds.
 * - Assumes prices are precomputed and rounded to 3 decimals
 *   (see Reports/index.jsx)
 * - Aggregates term bonds by term group for summary
 * - Computes total principal and cost per term
 * - No pricing or yield calculations performed here
 */
export default function BondSummaryReport({ rows = [] }) {
  const data = useMemo(() => {
    if (!rows?.length) return [];

    const termGroups = new Map();
    const out = [];

    for (const r of rows) {
      const isTerm = String(r.bond_type).toLowerCase() === "term";

      if (isTerm) {
        const groupKey = r.term_group || r.date;

        if (!termGroups.has(groupKey)) {
          termGroups.set(groupKey, {
            date: groupKey,
            callDate: "",
            principal: 0,
            coupon: null,
            yield: null,
            price: null,
            cost: null,
            bondType: "Term",
            latestDate: null,
            latestRow: null,
          });
        }

        const grp = termGroups.get(groupKey);
        const p = Number(r.principal);
        if (Number.isFinite(p)) grp.principal += p;

        if (!grp.latestDate || new Date(r.date) > new Date(grp.latestDate)) {
          grp.latestDate = r.date;
          grp.latestRow = r;
        }
      } else {
        const coupon = Number.isFinite(Number(r.coupon)) ? Number(r.coupon) : null;
        const yieldVal = Number.isFinite(Number(r.yield)) ? Number(r.yield) : null;

        const price = Number.isFinite(r.price)
          ? Number(fmtTrunc3(r.price))
          : null;

        const cost =
          Number.isFinite(price) && Number.isFinite(r.principal)
            ? (price / 100) * r.principal
            : null;

        out.push({
          date: r.date,
          callDate: r.call_date || "",
          principal: r.principal,
          coupon,
          yield: yieldVal,
          price,
          cost,
          bondType: "Serial",
        });
      }
    }

    for (const grp of termGroups.values()) {
      const final = grp.latestRow;

      if (final) {
        const coupon = Number.isFinite(Number(final.coupon))
          ? Number(final.coupon)
          : null;

        const yieldVal = Number.isFinite(Number(final.yield))
          ? Number(final.yield)
          : null;

        const price = Number.isFinite(final.price)
          ? Number(fmtTrunc3(final.price))
          : null;

        grp.date = final.date;
        grp.callDate = final.call_date || "";
        grp.coupon = coupon;
        grp.yield = yieldVal;
        grp.price = price;

        grp.cost =
          Number.isFinite(price) && Number.isFinite(grp.principal)
            ? (price / 100) * grp.principal
            : null;
      }

      out.push({
        date: grp.date,
        callDate: grp.callDate,
        principal: grp.principal,
        coupon: grp.coupon,
        yield: grp.yield,
        price: grp.price,
        cost: grp.cost,
        bondType: grp.bondType,
      });
    }

    out.sort((a, b) => new Date(a.date) - new Date(b.date));

    return out;
  }, [rows]);

  /** ---------- Grid Setup ---------- */

  const moneyFormat = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  const pctFormat = (v) =>
    Number.isFinite(v) ? Number(v).toFixed(3) : "";

  const rightAlign = "text-right numeric";

  const columns = [
    {
      key: "date",
      label: "Maturity",
      date: true,
      width: "w-28",
      className: "numeric",
    },
    {
      key: "callDate",
      label: "Call Date",
      date: true,
      width: "w-28",
      className: "numeric",
    },
    {
      key: "principal",
      label: "Principal",
      numeric: true,
      width: "w-32",
      className: rightAlign,
      headerClassName: "text-right",
      format: moneyFormat,
    },
    {
      key: "coupon",
      label: "Coupon",
      numeric: true,
      width: "w-24",
      className: rightAlign,
      headerClassName: "text-right",
      format: pctFormat,
    },
    {
      key: "yield",
      label: "Yield",
      numeric: true,
      width: "w-24",
      className: rightAlign,
      headerClassName: "text-right",
      format: pctFormat,
    },
    {
      key: "price",
      label: "Price",
      numeric: true,
      width: "w-24",
      className: rightAlign,
      headerClassName: "text-right",
      format: (v) => (Number.isFinite(v) ? fmtTrunc3(v) : ""),
    },
    {
      key: "cost",
      label: "Cost",
      numeric: true,
      width: "w-32",
      className: rightAlign,
      headerClassName: "text-right",
      format: moneyFormat,
    },
    {
      key: "bondType",
      label: "Bond Type",
      width: "w-24",
    },
  ];

  if (!data?.length) {
    return <div className="p-4 text-muted">No bond summary available.</div>;
  }

  return <ExcelGrid columns={columns} rows={data} />;
}